import { onBeforeUnmount, watch, type Ref } from 'vue'

interface DismissableOptions {
  /** Whether the overlay is currently visible, in every open mode it supports. */
  isOpen: Ref<boolean>
  /** Clears every piece of state that can hold the overlay open. */
  close: () => void
}

/** Dismisses an open overlay on an outside click or the Escape key. */
export const useDismissable = (
  container: Ref<HTMLElement | null>,
  { isOpen, close }: DismissableOptions
): void => {
  const closeOnOutsideClick = (event: MouseEvent): void => {
    const element = container.value
    if (element === null) return

    const { target } = event
    if (target instanceof Node && !element.contains(target)) close()
  }

  const closeOnEscape = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') close()
  }

  // Listeners exist only while the overlay is open, so a page with many
  // dismissable overlays does not accumulate document-level handlers.
  const setListening = (listening: boolean): void => {
    if (typeof document === 'undefined') return

    const apply = listening
      ? document.addEventListener.bind(document)
      : document.removeEventListener.bind(document)

    apply('click', closeOnOutsideClick as EventListener)
    apply('keydown', closeOnEscape as EventListener)
  }

  // `immediate` matters: an overlay that starts open would otherwise have no
  // listeners until something else changed its state, so the first Escape or
  // outside click would be ignored.
  watch(isOpen, setListening, { immediate: true })

  onBeforeUnmount(() => {
    setListening(false)
  })
}
